import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { toast } from 'react-toastify';

interface LoginPageProps {
  type?: 'ADMIN' | 'MEMBER';
}

export const LoginPage: React.FC<LoginPageProps> = ({ type = 'MEMBER' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email, password);

      // Redirect based on role and login type
      if (user?.role === 'ADMIN') {
        if (type === 'MEMBER') {
          toast.warn('Admin account detected. Redirecting to Admin Dashboard.');
        }
        navigate('/admin');
      } else {
        if (type === 'ADMIN') {
          toast.error('Access denied. Members cannot login here.');
          // We should probably logout or handle this better, but for now just show error
          return;
        }
        navigate('/member');
      }

      toast.success('Login successful!');
    } catch (error: any) {
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white font-sans">
      {/* Left Side - Visuals */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-indigo-900 opacity-90"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516574187841-693083f7e496?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80')] bg-cover bg-center mix-blend-overlay"></div>

        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
          <div>
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 mb-8">
              <span className="text-2xl font-bold">H</span>
            </div>
            <h1 className="text-4xl font-bold tracking-tight mb-4">Hospital Membership<br />Platform</h1>
            <p className="text-blue-100 text-lg max-w-md">Experience world-class healthcare management with our premium membership portal.</p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10">
            <p className="text-lg font-medium mb-4">"This platform has completely transformed how I manage my family's healthcare needs. The interface is stunning and easy to use."</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-400"></div>
              <div>
                <p className="font-bold">Dr. Sarah Smith</p>
                <p className="text-sm text-blue-200">Chief of Cardiology</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl shadow-slate-200 border border-slate-100">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-900">
              {type === 'ADMIN' ? 'Admin Portal' : 'Welcome Back'}
            </h2>
            <p className="mt-2 text-slate-500">
              Please enter your details to sign in
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label="Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="name@company.com"
                className="bg-slate-50 border-slate-200 focus:bg-white"
              />
              <div>
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="bg-slate-50 border-slate-200 focus:bg-white"
                  rightElement={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="focus:outline-none hover:text-indigo-600 transition-colors"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  }
                />
                <div className="flex justify-end mt-1">
                  <a href="#" className="text-sm font-medium text-primary-600 hover:text-primary-500">Forgot password?</a>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-lg shadow-primary-500/25 hover:shadow-primary-500/40"
              isLoading={isLoading}
            >
              Sign in
            </Button>

            {type === 'MEMBER' && (
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-slate-500">New to the platform?</span>
                </div>
              </div>
            )}

            {type === 'MEMBER' && (
              <Link to="/register" className="block w-full">
                <Button variant="secondary" className="w-full h-12" type="button">
                  Create an account
                </Button>
              </Link>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
